import React, { useEffect, useState } from 'react';
import { Modal, Table, Button, Space, Form, Input, DatePicker, Popconfirm, message, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { unifiedShipmentsAPI, TransitDocument, TransitDocumentPayload } from '../services/api.ts';

/**
 * Documenti di transito T1 di una spedizione EXTRA_UE (v3.10.0).
 * Solo riferimento (MRN, data, partenza, destinazione): il T1 non muove merce,
 * che entra in magazzino con l'Elabora delle dichiarazioni H2 (IM7) / H1 (IM4).
 * Facoltativo: non sempre il trasferimento porto → deposito avviene con un T1.
 */
interface Props {
  visible: boolean;
  shipment: { id: number; shipment_code: string } | null;
  canEdit: boolean;
  onClose: () => void;
}

const TransitDocumentsModal: React.FC<Props> = ({ visible, shipment, canEdit, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<TransitDocument[]>([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editing, setEditing] = useState<TransitDocument | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    if (!shipment) return;
    setLoading(true);
    try {
      const res = await unifiedShipmentsAPI.getTransitDocuments(shipment.id);
      setDocuments(res.documents || []);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nel caricamento dei T1');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, shipment?.id]);

  // Valori impostati dopo l'apertura del form (montato solo a finestra aperta)
  useEffect(() => {
    if (!formVisible) return;
    form.resetFields();
    form.setFieldsValue(editing
      ? { ...editing, document_date: editing.document_date ? dayjs(editing.document_date) : null }
      : { document_date: dayjs() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible, editing]);

  const handleSave = async () => {
    if (!shipment) return;
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload: TransitDocumentPayload = {
      document_number: values.document_number,
      document_date: values.document_date.format('YYYY-MM-DD'),
      origin_location: values.origin_location || null,
      destination_location: values.destination_location || null,
      notes: values.notes || null,
    };
    setSaving(true);
    try {
      const res = editing
        ? await unifiedShipmentsAPI.updateTransitDocument(shipment.id, editing.id, payload)
        : await unifiedShipmentsAPI.createTransitDocument(shipment.id, payload);
      message.success(res.message);
      setFormVisible(false);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nel salvataggio del T1');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc: TransitDocument) => {
    if (!shipment) return;
    try {
      const res = await unifiedShipmentsAPI.deleteTransitDocument(shipment.id, doc.id);
      message.success(res.message);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nell\'eliminazione del T1');
    }
  };

  const columns = [
    { title: 'Numero (MRN)', dataIndex: 'document_number', key: 'number', render: (v: string) => <b>{v}</b> },
    { title: 'Data', dataIndex: 'document_date', key: 'date', render: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY') : '-') },
    { title: 'Partenza', dataIndex: 'origin_location', key: 'origin', render: (v: string) => v || '-' },
    { title: 'Destinazione', dataIndex: 'destination_location', key: 'dest', render: (v: string) => v || '-' },
    { title: 'Note', dataIndex: 'notes', key: 'notes', render: (v: string) => v || '-' },
    ...(canEdit ? [{
      title: '', key: 'actions', width: 90,
      render: (_: any, r: TransitDocument) => (
        <Space size={0}>
          <Button type="text" icon={<EditOutlined />} onClick={() => { setEditing(r); setFormVisible(true); }} />
          <Popconfirm title={`Eliminare il T1 ${r.document_number}?`} onConfirm={() => handleDelete(r)} okText="Sì" cancelText="No">
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <Modal
      open={visible}
      title={`Documenti di transito T1 - Spedizione ${shipment?.shipment_code || ''}`}
      width={900}
      onCancel={onClose}
      footer={[<Button key="close" onClick={onClose}>Chiudi</Button>]}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Il T1 è il documento di transito con cui la merce non unionale va dal porto al deposito doganale. È facoltativo e solo di riferimento: non muove merce (la merce entra con l'Elabora delle dichiarazioni H2 / H1)."
      />
      {canEdit && (
        <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 8 }} onClick={() => { setEditing(null); setFormVisible(true); }}>
          Nuovo T1
        </Button>
      )}
      <Table rowKey="id" size="small" loading={loading} dataSource={documents} columns={columns} pagination={false}
        locale={{ emptyText: 'Nessun T1 registrato per questa spedizione' }} />

      <Modal
        open={formVisible}
        title={editing ? `Modifica T1 ${editing.document_number}` : 'Nuovo T1'}
        onCancel={() => setFormVisible(false)}
        onOk={handleSave}
        okText="Salva"
        cancelText="Annulla"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="document_number" label="Numero (MRN)" rules={[{ required: true, whitespace: true, message: 'Inserisci il numero del T1' }, { max: 50 }]}>
              <Input style={{ width: 260 }} />
            </Form.Item>
            <Form.Item name="document_date" label="Data" rules={[{ required: true, message: 'Inserisci la data' }]}>
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
          </Space>
          <Form.Item name="origin_location" label="Partenza (es. porto)" rules={[{ max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="destination_location" label="Destinazione (es. deposito doganale)" rules={[{ max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Note">
            <Input.TextArea rows={2} className="no-uppercase-modal" />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default TransitDocumentsModal;
